import React from 'react';

/**
 * `scrollWidth` и `clientWidth` округляются до целых пикселей, а реальная ширина дробная.
 * Без допуска таблица, помещающаяся в контейнер, периодически «скроллится» на пиксель
 * и мигает индикатором.
 */
const SUB_PIXEL_TOLERANCE = 1;

type HiddenContentState = {
  start: boolean;
  end: boolean;
};

export type UseHorizontalOverflowResult<ElementType extends HTMLElement> = {
  /**
  Ref на скроллящийся контейнер
  */
  scrollerReference: React.RefObject<ElementType | null>;
  /**
  Часть содержимого скрыта слева (контейнер прокручен вправо)
  */
  hasHiddenContentStart: boolean;
  /**
  Часть содержимого скрыта справа
  */
  hasHiddenContentEnd: boolean;
  /**
  Обработчик `onScroll` контейнера; можно звать вручную для пересчёта
  */
  handleScroll: () => void;
};

/**
 * Отслеживает, остаётся ли за границами горизонтально скроллящегося контейнера скрытое
 * содержимое — слева и справа. Нужен, чтобы нарисовать подсказку о прокрутке: на macOS и iOS
 * скроллбар оверлейный и появляется только во время скролла, поэтому обрезанная таблица
 * визуально неотличима от полной.
 *
 * Пересчёт происходит на скролл и на изменение размеров — как самого контейнера
 * (ресайз окна, схлопывание сайдбара), так и его содержимого (в таблице прибавилось колонок).
 *
 * Рассчитан на LTR: `scrollLeft` считается неотрицательным. В `dir="rtl"` браузеры отсчитывают
 * его от правого края (в т.ч. отрицательными значениями), и стороны подсказок разъедутся.
 * Приложение целиком LTR, поэтому RTL намеренно не поддержан — не поддержан честно, а не забыт.
 *
 * @example
 * const { scrollerReference, hasHiddenContentEnd, handleScroll } = useHorizontalOverflow<HTMLDivElement>();
 *
 * return (
 *   <Box sx={{ position: 'relative' }}>
 *     <Box ref={scrollerReference} onScroll={handleScroll} sx={{ overflowX: 'auto' }}>
 *       <Table />
 *     </Box>
 *     {hasHiddenContentEnd && <Box sx={fadeSx} />}
 *   </Box>
 * );
 */
export const useHorizontalOverflow = <
  ElementType extends HTMLElement,
>(): UseHorizontalOverflowResult<ElementType> => {
  const scrollerReference = React.useRef<ElementType>(null);
  const [hiddenContent, setHiddenContent] = React.useState<HiddenContentState>({
    start: false,
    end: false,
  });

  const handleScroll = React.useCallback(() => {
    const scroller = scrollerReference.current;
    if (!scroller) return;

    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    const maxScrollLeft = scrollWidth - clientWidth;
    const start = scrollLeft > SUB_PIXEL_TOLERANCE;
    const end = scrollLeft < maxScrollLeft - SUB_PIXEL_TOLERANCE;

    // Скролл стреляет на каждый кадр — не перерисовываем компонент, если ничего не изменилось
    setHiddenContent(previous =>
      previous.start === start && previous.end === end ? previous : { start, end },
    );
  }, []);

  const observerReference = React.useRef<ResizeObserver | undefined>(undefined);
  const observedContentReference = React.useRef<Element | undefined>(undefined);

  React.useEffect(() => {
    const scroller = scrollerReference.current;
    if (!scroller) return;

    handleScroll();

    const observer = new ResizeObserver(handleScroll);
    observer.observe(scroller);
    observerReference.current = observer;

    return () => {
      observer.disconnect();
      observerReference.current = undefined;
      observedContentReference.current = undefined;
    };
  }, [handleScroll]);

  // Ширина контейнера может не меняться, а содержимое — становиться шире, поэтому следим и за ним.
  // Содержимое при этом можно подменить (другая таблица) — без переподписки observer остался бы
  // висеть на удалённом узле и пропустил бы изменение ширины нового. Эффект без зависимостей:
  // сверяемся после каждого рендера, реальная работа делается только при смене узла.
  React.useEffect(() => {
    const scroller = scrollerReference.current;
    const observer = observerReference.current;
    if (!scroller || !observer) return;

    // `firstElementChild` даёт null — приводим к undefined, чтобы сравнение с ref было честным
    const content = scroller.firstElementChild ?? undefined;
    if (content === observedContentReference.current) return;

    if (observedContentReference.current) observer.unobserve(observedContentReference.current);
    if (content) observer.observe(content);
    observedContentReference.current = content;
    handleScroll();
  });

  return {
    scrollerReference,
    hasHiddenContentStart: hiddenContent.start,
    hasHiddenContentEnd: hiddenContent.end,
    handleScroll,
  };
};
